import { useMemo } from "react";
import { ScrollView, StyleSheet } from "react-native";
import { useTranslation } from "react-i18next";
import {
  coerceLocale,
  type CoachAthleteCard,
  type UnitsPreference,
} from "@jackedlogs/shared";
import {
  AthleteDetail,
  TargetsEditor,
  createSessionResultsCache,
  makeAthleteScopedApiFetch,
  resolveTheme,
  useAthleteDetail,
  useTargetsEditorSwap,
  type ProgressDeps,
} from "@jackedlogs/ui";

import { apiFetch } from "./api";
import { useThemePalette } from "./theme";

// Thin web-rnw mount of the shared <AthleteDetail> (CO-9). The shared Progress
// panels inside it self-fetch through an ATHLETE-SCOPED ProgressDeps: apiFetch
// wrapped so the allowlisted reads hit the CO-11 edge-gated routes, plus a
// FRESH per-athlete results cache (the module-level Progress cache belongs to
// the coach's own athlete surface — sharing it would leak one partition's
// numbers into the other). No figure: the athlete's preferredFigure is outside
// the coach profile allowlist, and the coverage figure is optional.
//
// CO-7 R4: the targets editor is a SWAP inside this host — rendered OUTSIDE the
// ScrollView (its top bar stays sticky) and in place of <AthleteDetail>, keeping
// the SAME memoized `deps` so the warmed session-results cache survives the
// round-trip (a new route would drop it and spinner the reference numbers). The
// base (unscoped) apiFetch carries the ETag write-lock; the athlete-scoped one
// is read-only by construction and stays out of the write.
export function AthleteDetailScreen({
  card,
  onBack,
}: {
  card: CoachAthleteCard;
  onBack: () => void;
}) {
  const { dark, family } = useThemePalette();
  const theme = resolveTheme(dark, family);
  const { t, i18n } = useTranslation();
  const locale = coerceLocale(i18n.language);
  // The base (unscoped) client: this hook's reads — /api/profiles/:sub,
  // /api/me/programs, the MAX map — carry ?athleteSub themselves and are NOT
  // in athleteScopedApi's allowlist, so the scoped wrapper would throw on them.
  const {
    profile,
    programs,
    maxes,
    athleteTargets: loadedTargets,
  } = useAthleteDetail(apiFetch, card.sub);

  // The detail ↔ editor swap + targets-refresh-on-save (shared with mobile).
  const { targets, editing, openEditor, closeEditor, onSaved } =
    useTargetsEditorSwap(loadedTargets);

  const scopedApi = useMemo(
    () => makeAthleteScopedApiFetch(apiFetch, card.sub),
    [card.sub],
  );
  // Fresh single-flight cache per athlete — keyed on the sub so switching
  // athletes can never serve the previous athlete's snapshot.
  const resultsCache = useMemo(
    () => createSessionResultsCache(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [card.sub],
  );

  const units: UnitsPreference = profile?.unitsPreference ?? "kg";
  const deps = useMemo<ProgressDeps>(
    () => ({
      apiFetch: scopedApi,
      theme,
      units,
      painMap: {},
      resultsCache,
      isDark: dark,
      weekStartDay: profile?.weekStartDay,
    }),
    [scopedApi, theme, units, resultsCache, dark, profile?.weekStartDay],
  );

  if (editing) {
    return (
      <TargetsEditor
        theme={theme}
        apiFetch={apiFetch}
        deps={deps}
        source={{
          athleteSub: card.sub,
          onSaved,
        }}
        // On the coach's screen the athlete's own edits carry their name; every
        // other author is the coach reading it.
        resolveAuthor={(bySub) =>
          bySub === card.sub
            ? card.name
            : t("coach.targets.historyByYou", { defaultValue: "You" })
        }
        units={units}
        weekStartDay={profile?.weekStartDay}
        locale={locale}
        backLabel={card.name}
        onClose={closeEditor}
      />
    );
  }

  return (
    <ScrollView contentContainerStyle={styles.content}>
      <AthleteDetail
        theme={theme}
        card={card}
        deps={deps}
        programs={programs}
        currentProgramId={profile?.currentProgramId}
        weekStartDay={profile?.weekStartDay}
        maxes={maxes}
        athleteTargets={targets}
        onBack={onBack}
        onEditTargets={openEditor}
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  content: { paddingBottom: 48 },
});
